import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Package, Users, ShoppingCart, TrendingUp } from 'lucide-react';
import PriceBControl from '../components/PriceBControl';
import BranchSummary from '../components/BranchSummary';
import CreditDashboard from '../components/CreditDashboard';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isManager = ['master', 'superadmin', 'admin'].includes(user?.role_id);

  const loadStats = async () => {
    try {
      const response = await api.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => { await loadStats(); })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-sm text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Ventas',
      value: stats?.total_sales || 0,
      icon: ShoppingCart,
      color: '#00BFA5',
      testId: 'stat-total-sales',
      link: '/sales'
    },
    {
      title: 'Productos',
      value: stats?.total_products || 0,
      icon: Package,
      color: '#10B981',
      testId: 'stat-total-products',
      link: '/inventory'
    },
    {
      title: 'Clientes',
      value: stats?.total_clients || 0,
      icon: Users,
      color: '#F59E0B',
      testId: 'stat-total-clients',
      link: '/clients'
    },
    {
      title: 'Usuarios',
      value: stats?.total_users || 0,
      icon: TrendingUp,
      color: '#3B82F6',
      testId: 'stat-total-users',
      link: '/users'
    },
  ];

  if (['master', 'superadmin', 'admin', 'vendedor'].includes(user?.role_id)) return <CreditDashboard isManager={isManager} />;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#022C22] mb-2">
          Bienvenido, {user?.full_name}
        </h1>
        <p className="text-[#64748B]">
          Aquí está el resumen de tu negocio
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={stat.title} 
              className="border border-[#E2E8F0] shadow-sm cursor-pointer hover:shadow-md transition-shadow" 
              data-testid={stat.testId}
              onClick={() => navigate(stat.link)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-[#64748B]">
                  {stat.title}
                </CardTitle>
                <Icon size={20} style={{ color: stat.color }} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-black text-[#022C22]">
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Precio B — control global (admin/master) */}
      {isManager && <PriceBControl />}
      {['master', 'superadmin'].includes(user?.role_id) && <BranchSummary />}

      {/* Sales by Status */}
      {stats?.sales_by_status && (
        <Card className="border border-[#E2E8F0] shadow-sm" data-testid="sales-by-status-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl font-bold">Ventas por Estado</CardTitle>
            <p className="text-xs text-[#94A3B8]">Toca una tarjeta para ver esas ventas filtradas</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { key: 'presupuesto', label: 'Presupuestos', color: '#9333EA' },
                { key: 'abierto', label: 'Abiertas', color: '#022C22' },
                { key: 'procesada', label: 'Procesadas', color: '#4338CA' },
                { key: 'pendiente_despacho', label: 'Pend. Despacho', color: '#F59E0B' },
                { key: 'en_transito', label: 'En Tránsito', color: '#3B82F6' },
                { key: 'despachado', label: 'Entregadas', color: '#10B981' },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => navigate(`/sales?status=${s.key}`)}
                  className="p-4 bg-[#F8F9FA] rounded-md text-left hover:bg-[#EEF2FF] hover:shadow-md transition-all border border-transparent hover:border-[#C7D2FE]"
                  data-testid={`status-${s.key}`}
                >
                  <p className="text-xs text-[#64748B] mb-1">{s.label}</p>
                  <p className="text-2xl font-heading font-bold" style={{ color: s.color }}>
                    {stats.sales_by_status[s.key] || 0}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Stats by Company (Solo Master) */}
      {user?.role_id === 'master' && stats?.companies && stats.companies.length > 0 && (
        <Card className="border border-[#E2E8F0] shadow-sm" data-testid="companies-stats-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl font-bold">
              Resumen por Empresa (Ordenado por Ventas) - Solo Activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.companies
                .filter(company => company.active !== false)
                .map((company, index) => (
                <div key={company.company_id} className="p-4 bg-[#F8F9FA] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-heading font-black text-[#022C22]">
                        #{index + 1}
                      </span>
                      <h3 className="font-heading text-lg font-bold text-[#022C22]">
                        {company.company_name}
                      </h3>
                    </div>
                    <span className="text-xl font-heading font-black text-primary">
                      ${company.total_sales_amount.toFixed(2)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                    <div className="text-center">
                      <p className="text-xs text-[#64748B]">Ventas</p>
                      <p className="text-lg font-bold text-[#022C22]">{company.total_sales}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748B]">Productos</p>
                      <p className="text-lg font-bold text-[#022C22]">{company.total_products}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748B]">Clientes</p>
                      <p className="text-lg font-bold text-[#022C22]">{company.total_clients}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748B]">Usuarios</p>
                      <p className="text-lg font-bold text-[#022C22]">{company.total_users}</p>
                    </div>
                  </div>

                  {/* Estado de facturas/pedidos — botones con mejor presencia */}
                  <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                    <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-2">Por estado</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                      {company.status_breakdown && [
                        ['presupuesto', 'Presupuestos', '#9333EA', 'bg-purple-50'],
                        ['abierto', 'Abiertas', '#022C22', 'bg-slate-50'],
                        ['procesada', 'Procesadas', '#4338CA', 'bg-indigo-50'],
                        ['pendiente_despacho', 'Pend. despacho', '#D97706', 'bg-orange-50'],
                        ['en_transito', 'En tránsito', '#2563EB', 'bg-blue-50'],
                        ['despachado', 'Entregadas', '#16A34A', 'bg-green-50'],
                      ].map(([key, label, color, bg]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => navigate(`/sales?company=${company.company_id}&status=${key}`)}
                          className={`${bg} rounded-xl px-3 py-2.5 text-left border border-transparent hover:border-[#CBD5E1] hover:shadow-md hover:-translate-y-0.5 transition-all`}
                          data-testid={`company-status-${company.company_id}-${key}`}
                        >
                          <p className="text-[11px] text-[#64748B] leading-tight mb-0.5">{label}</p>
                          <p className="text-xl font-heading font-black leading-none" style={{ color }}>{company.status_breakdown[key] || 0}</p>
                        </button>
                      ))}
                    </div>

                    {/* Por tipo de documento */}
                    {company.doc_type_breakdown && (
                      <>
                        <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-2 mt-3">Por documento</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            ['factura', 'Facturas', '#022C22', 'bg-slate-50'],
                            ['nota_entrega', 'Notas de entrega', '#0891B2', 'bg-cyan-50'],
                            ['presupuesto', 'Presupuestos', '#9333EA', 'bg-purple-50'],
                          ].map(([key, label, color, bg]) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => navigate(`/sales?company=${company.company_id}&doc=${key}`)}
                              className={`${bg} rounded-xl px-3 py-2.5 text-left border border-transparent hover:border-[#CBD5E1] hover:shadow-md hover:-translate-y-0.5 transition-all`}
                              data-testid={`company-doc-${company.company_id}-${key}`}
                            >
                              <p className="text-[11px] text-[#64748B] leading-tight mb-0.5">{label}</p>
                              <p className="text-xl font-heading font-black leading-none" style={{ color }}>{company.doc_type_breakdown[key] || 0}</p>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-[#64748B]">
                    <span>Facturas abiertas: <b className="text-[#022C22]">{company.open_invoices ?? 0}</b></span>
                    {company.total_pending != null && (
                      <span>Por cobrar: <b className="text-red-600">${(company.total_pending || 0).toFixed(2)}</b></span>
                    )}
                    {company.total_paid != null && (
                      <span>Cobrado: <b className="text-green-700">${(company.total_paid || 0).toFixed(2)}</b></span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}